import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('[supabase] Missing env vars — using mock mode')
}

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null

// ============================================================
// Auth
// ============================================================

export async function signIn(email, password) {
  if (!supabase) throw new Error('Supabase not configured')
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

export async function signOut() {
  if (!supabase) return
  await supabase.auth.signOut()
}

export async function getSession() {
  if (!supabase) return null
  const { data } = await supabase.auth.getSession()
  return data.session
}

export async function getAccessToken() {
  const session = await getSession()
  return session?.access_token || null
}

export function onAuthChange(cb) {
  if (!supabase) return () => {}
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => cb(session))
  return () => subscription.unsubscribe()
}

/**
 * Guard: if no session, redirect to #/admin/login. Otherwise call cb(session).
 */
export async function requireAuth(cb) {
  const session = await getSession()
  if (!session) {
    window.location.hash = '#/admin/login'
    return
  }
  await cb(session)
}

// ============================================================
// Storage — product images
// ============================================================

const BUCKET = 'product-images'

export async function uploadProductImage(file) {
  if (!supabase) throw new Error('Supabase not configured')
  const ext = file.name.split('.').pop()
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false })
  if (error) throw error
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return data.publicUrl
}

export async function deleteProductImage(url) {
  if (!supabase) return
  // Extract path from public URL
  const marker = `/object/public/${BUCKET}/`
  const idx = url.indexOf(marker)
  if (idx === -1) return
  const path = url.slice(idx + marker.length)
  await supabase.storage.from(BUCKET).remove([path])
}

export async function getCategories() {
  if (!supabase) return ['discs', 'flap-discs', 'cut-off', 'kits']
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('sort_order', { ascending: true })
  if (error) {
    // Fall back to deriving from products if categories table doesn't exist yet
    const { data: products } = await supabase.from('products').select('category')
    return [...new Set((products || []).map(p => p.category).filter(Boolean))].sort()
  }
  return data
}

export async function createCategory(data) {
  if (!supabase) throw new Error('Supabase not configured')
  const { data: created, error } = await supabase
    .from('categories')
    .insert(data)
    .select()
    .single()
  if (error) throw error
  return created
}

export async function updateCategory(id, data) {
  if (!supabase) throw new Error('Supabase not configured')
  const { data: updated, error } = await supabase
    .from('categories')
    .update(data)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return updated
}

export async function deleteCategory(id) {
  if (!supabase) throw new Error('Supabase not configured')
  const { error } = await supabase.from('categories').delete().eq('id', id)
  if (error) throw error
}

// ============================================================
// Customer queries
// ============================================================

export async function getCustomers() {
  if (!supabase) return []
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function getCustomer(id) {
  if (!supabase) return null
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw error
  return data
}

export async function updateCustomerNotes(id, notes) {
  if (!supabase) throw new Error('Supabase not configured')
  const { error } = await supabase.from('customers').update({ notes }).eq('id', id)
  if (error) throw error
}

export async function getOrdersByEmail(email) {
  if (!supabase) return []
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('customer_email', email)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

// ============================================================
// Admin product queries (no active filter)
// ============================================================

export async function getAllProducts() {
  if (!supabase) return getMockProducts()
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .order('sort_order', { ascending: true })
  if (error) throw error
  return data
}

export async function createProduct(data) {
  if (!supabase) throw new Error('Supabase not configured')
  const { data: created, error } = await supabase
    .from('products')
    .insert(data)
    .select('id')
    .single()
  if (error) throw error
  return created
}

export async function updateProduct(id, data) {
  if (!supabase) throw new Error('Supabase not configured')
  const { error } = await supabase.from('products').update(data).eq('id', id)
  if (error) throw error
}

export async function deleteProduct(id) {
  if (!supabase) throw new Error('Supabase not configured')
  const { error } = await supabase.from('products').delete().eq('id', id)
  if (error) throw error
}

/**
 * Fetch all active products
 */
export async function getProducts({ category } = {}) {
  if (!supabase) return getMockProducts()

  let query = supabase
    .from('products')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true })

  if (category && category !== 'all') {
    query = query.eq('category', category)
  }

  const { data, error } = await query
  if (error) throw error
  return data
}

/**
 * Fetch single product by ID
 */
export async function getProduct(id) {
  if (!supabase) return getMockProducts().find(p => p.id === id) || null

  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('id', id)
    .eq('active', true)
    .single()

  if (error) throw error
  return data
}

// ============================================================
// Blog CRUD
// ============================================================

export async function getBlogPosts() {
  if (!supabase) return []
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function getBlogPost(id) {
  if (!supabase) return null
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw error
  return data
}

export async function createBlogPost(post) {
  if (!supabase) throw new Error('Supabase not configured')
  const { data, error } = await supabase
    .from('blog_posts')
    .insert(post)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function updateBlogPost(id, updates) {
  if (!supabase) throw new Error('Supabase not configured')
  const { data, error } = await supabase
    .from('blog_posts')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteBlogPost(id) {
  if (!supabase) throw new Error('Supabase not configured')
  const { error } = await supabase.from('blog_posts').delete().eq('id', id)
  if (error) throw error
}

/**
 * Fetch single product by slug
 */
export async function getProductBySlug(slug) {
  if (!supabase) return getMockProducts().find(p => p.slug === slug) || null

  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('slug', slug)
    .eq('active', true)
    .single()

  if (error) throw error
  return data
}

/**
 * Mock products for local development without Supabase
 */
function getMockProducts() {
  return [
    {
      id: 'mock-1',
      slug: 'carbon-series-7-grinding-disc',
      name: 'Carbon Series 7" Grinding Disc',
      description: 'Our flagship 7-inch carbon-infused grinding disc. Engineered for maximum material removal with minimal vibration. The carbon-fiber composite matrix provides superior heat dissipation and extends disc life by up to 3x.',
      price_cents: 4999,
      images: [],
      category: 'discs',
      stock_qty: 150,
      sku: 'APL-GD-7-C',
      active: true,
      metadata: { diameter: '7"', arbor: '5/8"-11', max_rpm: 8500 },
      created_at: new Date().toISOString(),
    },
    {
      id: 'mock-2',
      slug: 'carbon-series-45-grinding-disc',
      name: 'Carbon Series 4.5" Grinding Disc',
      description: 'Compact powerhouse for tight-space grinding. Same carbon-infused technology as our 7-inch in a versatile 4.5-inch format. Perfect for finishing and detail work.',
      price_cents: 2999,
      images: [],
      category: 'discs',
      stock_qty: 220,
      sku: 'APL-GD-45-C',
      active: true,
      metadata: { diameter: '4.5"', arbor: '5/8"-11', max_rpm: 13300 },
      created_at: new Date().toISOString(),
    },
    {
      id: 'mock-3',
      slug: 'carbon-flap-disc-40-grit',
      name: 'Carbon Flap Disc — 40 Grit',
      description: 'Heavy-duty flap disc for aggressive stock removal and blending. Carbon-reinforced backing plate with zirconia alumina abrasive. Outlasts conventional flap discs by 2x.',
      price_cents: 3499,
      images: [],
      category: 'flap-discs',
      stock_qty: 85,
      sku: 'APL-FD-40-C',
      active: true,
      metadata: { grit: 40, diameter: '4.5"', type: 'Type 29' },
      created_at: new Date().toISOString(),
    },
    {
      id: 'mock-4',
      slug: 'carbon-flap-disc-80-grit',
      name: 'Carbon Flap Disc — 80 Grit',
      description: 'Medium-grit finishing disc for blending weld seams and surface preparation. Smooth finish with minimal chatter. Carbon backing ensures consistent performance throughout disc life.',
      price_cents: 3499,
      images: [],
      category: 'flap-discs',
      stock_qty: 110,
      sku: 'APL-FD-80-C',
      active: true,
      metadata: { grit: 80, diameter: '4.5"', type: 'Type 27' },
      created_at: new Date().toISOString(),
    },
    {
      id: 'mock-5',
      slug: 'carbon-cut-off-disc-5-pack',
      name: 'Carbon Cut-Off Disc — 5-Pack',
      description: 'Precision cut-off wheels with carbon-reinforced mesh. Ultra-thin 0.045" kerf minimizes material waste. Stays cooler and cuts faster than standard wheels.',
      price_cents: 1999,
      images: [],
      category: 'cut-off',
      stock_qty: 300,
      sku: 'APL-CO-5PK-C',
      active: true,
      metadata: { thickness: '0.045"', diameter: '4.5"', qty: 5 },
      created_at: new Date().toISOString(),
    },
    {
      id: 'mock-6',
      slug: 'professional-starter-kit',
      name: 'Professional Starter Kit',
      description: 'Everything you need to experience the Airplaten difference. Includes two 7" grinding discs, two 4.5" flap discs (40 and 80 grit), and a 5-pack of cut-off wheels. Shipped in a branded carrying case.',
      price_cents: 12999,
      images: [],
      category: 'kits',
      stock_qty: 40,
      sku: 'APL-KIT-PRO',
      active: true,
      metadata: { includes: '7 pieces', case: true },
      created_at: new Date().toISOString(),
    },
  ]
}
